import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Check, X } from "lucide-react";

/**
 * PlanComparisonTable
 * 
 * O que faz:
 * - Exibe tabela comparativa detalhada entre os planos Basic, Pro e Premium
 * - Facilita a decisão do usuário mostrando diferenças entre os planos
 * 
 * Onde é usado:
 * - Na LandingPage, após a seção de preços
 * 
 * Como se conecta:
 * - Componente visual isolado que complementa a PricingTable
 */

interface Feature {
  name: string;
  basic: boolean;
  pro: boolean;
  premium: boolean;
  basicValue?: string;
  proValue?: string;
  premiumValue?: string;
}

// Mantém nomes e disponibilidade alinhados aos cards e às permissões atuais do produto.
const features: Feature[] = [
  {
    name: "WhatsApp integrado",
    basic: true,
    pro: true,
    premium: true,
  },
  {
    name: "IA de atendimento",
    basic: true,
    pro: true,
    premium: true,
    basicValue: "Básica",
    proValue: "Completa",
    premiumValue: "Completa"
  },
  {
    name: "Rastreamento de anúncios",
    basic: true,
    pro: true,
    premium: true,
  },
  {
    name: "Links personalizados",
    basic: false,
    pro: true,
    premium: true,
  },
  {
    name: "Funil visual (Kanban)",
    basic: true,
    pro: true,
    premium: true,
  },
  {
    name: "Agenda de consultas",
    basic: true,
    pro: true,
    premium: true,
  },
  {
    name: "Follow-up automático",
    basic: false,
    pro: false,
    premium: true,
  },
  {
    name: "Relatórios de IA",
    basic: true,
    pro: true,
    premium: true,
    basicValue: "2/mês",
    proValue: "4/mês",
    premiumValue: "Ilimitado"
  },
  {
    name: "Import/Export contatos",
    basic: false,
    pro: true,
    premium: true,
  },
  {
    name: "Disparo programado",
    basic: false,
    pro: true,
    premium: true,
  },
  {
    name: "Chat do Instagram",
    basic: false,
    pro: true,
    premium: true,
  },
  {
    name: "Prontuário médico",
    basic: false,
    pro: false,
    premium: true,
  },
  {
    name: "IA de agendamento automático",
    basic: false,
    pro: false,
    premium: true,
  },
  {
    name: "Suporte",
    basic: true,
    pro: true,
    premium: true,
    basicValue: "Padrão",
    proValue: "Padrão",
    premiumValue: "Prioritário"
  },
];

export const PlanComparisonTable: React.FC = () => {
  return (
    <Card className="max-w-5xl mx-auto border-2 border-gray-200 rounded-2xl overflow-hidden">
      <CardContent className="p-0">
        {/* Header da tabela - Desktop */}
        <div className="hidden md:grid md:grid-cols-4 bg-gradient-to-r from-emerald-50 to-emerald-100 border-b border-emerald-200">
          <div className="p-6 font-bold text-gray-900 text-lg">
            Recurso
          </div>
          <div className="p-6 text-center border-l border-emerald-200">
            <div className="font-bold text-gray-900 text-xl mb-1">Basic</div>
            <div className="text-emerald-600 font-semibold">R$ 67/mês</div>
          </div>
          <div className="p-6 text-center border-l border-emerald-200 bg-emerald-600 text-white relative">
            <div className="absolute top-0 right-0 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded-bl">
              MAIS RECURSOS
            </div>
            <div className="font-bold text-xl mb-1">Pro</div>
            <div className="font-semibold">R$ 147/mês</div>
          </div>
          <div className="p-6 text-center border-l border-emerald-200">
            <div className="font-bold text-gray-900 text-xl mb-1">Premium</div>
            <div className="text-emerald-600 font-semibold">R$ 247/mês</div>
          </div>
        </div>

        {/* Corpo da tabela - Desktop */}
        <div className="hidden md:block">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`grid grid-cols-4 border-b border-gray-200 ${
                index % 2 === 0 ? "bg-white" : "bg-gray-50"
              }`}
            >
              <div className="p-4 font-medium text-gray-900">{feature.name}</div>
              <div className="p-4 flex items-center justify-center border-l border-gray-200">
                {feature.basic ? (
                  feature.basicValue ? (
                    <span className="text-sm font-semibold text-emerald-600">
                      {feature.basicValue}
                    </span>
                  ) : (
                    <Check className="text-emerald-600" size={24} />
                  )
                ) : (
                  <X className="text-gray-400" size={24} />
                )}
              </div>
              <div className="p-4 flex items-center justify-center border-l border-gray-200 bg-emerald-50">
                {feature.pro ? (
                  feature.proValue ? (
                    <span className="text-sm font-semibold text-emerald-600">
                      {feature.proValue}
                    </span>
                  ) : (
                    <Check className="text-emerald-600" size={24} />
                  )
                ) : (
                  <X className="text-gray-400" size={24} />
                )}
              </div>
              <div className="p-4 flex items-center justify-center border-l border-gray-200">
                {feature.premium ? (
                  feature.premiumValue ? (
                    <span className="text-sm font-semibold text-emerald-600">
                      {feature.premiumValue}
                    </span>
                  ) : (
                    <Check className="text-emerald-600" size={24} />
                  )
                ) : (
                  <X className="text-gray-400" size={24} />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Versão mobile - Cards empilhados */}
        <div className="md:hidden">
          {["basic", "pro", "premium"].map((plan) => (
            <div key={plan} className="border-b-8 border-gray-200 last:border-b-0">
              <div className={`p-4 font-bold text-center ${
                plan === "pro" 
                  ? "bg-emerald-600 text-white" 
                  : "bg-emerald-100 text-gray-900"
              }`}>
                <div className="text-xl mb-1">
                  {plan === "basic" && "Basic"}
                  {plan === "pro" && "Pro"}
                  {plan === "premium" && "Premium"}
                </div>
                <div className={plan === "pro" ? "text-white" : "text-emerald-600"}>
                  {plan === "basic" && "R$ 67/mês"}
                  {plan === "pro" && "R$ 147/mês"}
                  {plan === "premium" && "R$ 247/mês"}
                </div>
                {plan === "pro" && (
                  <div className="mt-2 bg-yellow-400 text-yellow-900 text-xs font-bold inline-block px-3 py-1 rounded-full">
                    MAIS RECURSOS
                  </div>
                )}
              </div>
              
              <div className="p-4 space-y-3">
                {features.map((feature, index) => {
                  const hasFeature = feature[plan as keyof typeof feature];
                  const value = feature[`${plan}Value` as keyof typeof feature];
                  
                  return (
                    <div key={index} className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-700">{feature.name}</span>
                      {hasFeature ? (
                        value ? (
                          <span className="text-sm font-semibold text-emerald-600">
                            {value}
                          </span>
                        ) : (
                          <Check className="text-emerald-600" size={20} />
                        )
                      ) : (
                        <X className="text-gray-400" size={20} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
